import { openMessageAfter } from '../lib/whatsapp';
import { useEffect, useState } from 'react';
import { useDataStore } from '../store/dataStore';
import { formatDateTime, notificationChannels } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Bell,
  MessageSquare,
  Phone,
  Mail,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle
} from 'lucide-react';

export default function NotificationsPage() {
  const {
    notifications,
    notificationsLoading,
    fetchNotifications,
    sendNotification,
    customers,
    fetchCustomers
  } = useDataStore();

  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    message: '',
    channel: 'sms'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchCustomers();
  }, [fetchNotifications, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.message) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    try {
      const cust = customers.find(c => c.id === formData.customer_id);
      await openMessageAfter(formData.channel, async () => {
        await sendNotification(formData.customer_id, formData.message, formData.channel);
        return { phone: cust?.bride_phone || cust?.groom_phone, text: formData.message };
      });
      toast.success(`${formData.channel === 'sms' ? 'SMS' : 'WhatsApp'} açıldı — mesaj hazır, göndere basın`);
      setIsOpen(false);
      setFormData({
        customer_id: '',
        message: '',
        channel: 'sms'
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms': return Phone;
      case 'whatsapp': return MessageSquare;
      case 'email': return Mail;
      default: return Bell;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'pending': return <Clock className="h-4 w-4 text-amber-600" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Bildirimler</h1>
          <p className="text-muted-foreground mt-1">SMS ve WhatsApp bildirimleri gönderin</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="send-notification-btn">
              <Plus className="h-4 w-4 mr-2" />
              Bildirim Gönder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Bildirim Gönder</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Müşteri *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                >
                  <SelectTrigger data-testid="notification-customer-select">
                    <SelectValue placeholder="Müşteri seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.bride_name} & {customer.groom_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gönderim Kanalı *</Label>
                <Select
                  value={formData.channel}
                  onValueChange={(value) => setFormData({ ...formData, channel: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">E-posta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mesaj *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Göndermek istediğiniz mesajı yazın..."
                  rows={4}
                  data-testid="notification-message-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="notification-submit-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Gönder'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mock Warning */}
      <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200">Demo Modu</p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              SMS ve WhatsApp gönderimi şu an simule ediliyor. Gerçek gönderim için Twilio API anahtarlarınızı eklemeniz gerekiyor.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      {notificationsLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : notifications.length > 0 ? (
        <div className="space-y-4">
          {notifications.map((notification, index) => {
            const ChannelIcon = getChannelIcon(notification.channel);
            return (
              <Card key={notification.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ChannelIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{notification.customer_name}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                          {notificationChannels[notification.channel]}
                        </span>
                        {getStatusIcon(notification.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{notification.customer_phone}</p>
                      <p className="text-sm bg-muted/50 p-3 rounded-lg">{notification.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatDateTime(notification.sent_at || notification.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Henüz bildirim yok</h3>
          <p className="text-muted-foreground mb-4">Müşterilerinize bildirim göndermeye başlayın</p>
        </div>
      )}
    </div>
  );
}
